import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft } from 'lucide-react'
import { Input } from '@/components/ui_custom/input'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/common/logo'
import { useLoader } from '@/contexts/LoaderContext'
import { toast } from '@/hooks/use-toast'
import { api } from '@/lib/axios'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { ForgotPasswordSchema, forgotPasswordSchema } from '@/entities/user'

const loc = 'pages/ForgotPassword'

export default function ForgotPassword() {
  const [code, setCode] = useState('')
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema)
  })

  const formValues = form.watch()

  const _handleSendEmail = async () => {
    try {
      showLoader()

      const response = await api.post('/', {
        ...formValues,
        action: 'forgot-password'
      })

      if ([HTTP_STATUS_CODE.OK].includes(response.data?.statusCode)) {
        toast({ variant: 'success', title: 'Sucesso', description: 'Link de redefinição enviado com sucesso.' })
        setSubmitted(true)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível enviar o link de redefinição.' })
    } catch (error) {
      console.error(`Unhandled error at ${loc}.handleSendEmail. Details: ${error}`)
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível enviar o link de redefinição.' })
    } finally {
      hideLoader()
    }
  }

  return (
    <div className="app-shell min-h-screen min-w-full flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-card p-8 rounded-3xl shadow-elevated border border-border/50">
        <div className="flex justify-center items-center cursor-pointer" onClick={() => (window.location.href = '/')}>
          <Logo size="md" invert={true} />
        </div>

        <h2 className="mt-6 text-center text-2xl font-bold text-foreground">Redefinir sua senha</h2>

        {submitted ? (
          <div className="mt-8 space-y-6 flex flex-col items-center">
            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4">
              <p className="text-sm text-green-700 dark:text-green-400">Se existir uma conta com o e-mail {formValues.email}, você receberá um link para redefinir sua senha.</p>
              <p className="text-sm text-green-700 dark:text-green-400">Não esqueca de verificar sua caixa de spam.</p>
            </div>

            <Link to="/login" className="w-full">
              <Button className="w-full" type="button">
                <ArrowLeft className="h-5 w-5" />
                Voltar
              </Button>
            </Link>

            <p className="text-sm text-center">
              Não recebeu o e-mail?{' '}
              <p className="font-medium text-primary hover:brightness-110 cursor-pointer transition-all" onClick={() => setSubmitted(false)}>
                Tente novamente.
              </p>
            </p>
          </div>
        ) : (
          <>
            <p className="mt-2 text-center text-sm text-muted-foreground">Digite seu endereço de e-mail e lhe enviaremos um link para redefinir sua senha.</p>

            <Form {...form}>
              <form
                className="mt-8 space-y-6"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    _handleSendEmail()
                  }
                }}
              >
                <FormField
                  control={form.control}
                  name={'email'}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormControl>
                        <Input {...field} id="email" name="email" type="email" autoComplete="email" required label="E-mail" labelClass="text-foreground font-medium" leftIcon={<Mail className="h-5 w-5" />} />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="button" className="w-full" loading={isLoading} onClick={_handleSendEmail}>
                  Enviar link de redefinição
                </Button>

                <div className="text-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Lembra-se da sua senha ?{' '}
                    <Link to="/login" className="font-semibold text-primary hover:brightness-110 transition-all">
                      Entrar
                    </Link>
                  </p>
                </div>
              </form>
            </Form>
          </>
        )}
      </div>
    </div>
  )
}
